'use client';

import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    BoxProps,
    Grid,
    HTMLChakraProps,
    IconButton,
    useBreakpointValue,
} from '@chakra-ui/react';
// Here we have used react-icons package for the icons
import { BiLeftArrowAlt as BiLeftArrowAlt2, BiRightArrowAlt as BiRightArrowAlt2 } from 'react-icons/bi';
// And react-slick as our Carousel Lib
import { Modal } from '@mui/material';
import { Image } from './Image';

// eslint-disable-next-line no-undef
const BiLeftArrowAlt = BiLeftArrowAlt2 as () => JSX.Element;
// eslint-disable-next-line no-undef
const BiRightArrowAlt = BiRightArrowAlt2 as () => JSX.Element;

// Settings for the slider

type SimpleCarouselProps = {
    images: string[]
} & BoxProps & HTMLChakraProps<'div'>

export const SimpleCarousel: React.FC<SimpleCarouselProps> = ({
    images: cards,
    ...props
}) => {
    // As we have used custom buttons, we need a reference variable to
    // change the state

    // These are the breakpoints which changes the position of the
    // buttons as the screen size changes
    const top = useBreakpointValue({ base: '90%', md: '50%' });
    const side = useBreakpointValue({ base: '30%', md: '40px' });

    const [open, setOpen] = useState(false);
    const [currSrc, setCurrSrc] = useState('');

    const handleOpen = (src: string) => {
        setOpen(true);
        setCurrSrc(src);
    };
    const handleClose = () => setOpen(false);

    const [value, setValue] = useState(0);

    const ref = useRef<HTMLDivElement>();

    useEffect(() => {
        if (ref.current) {
            const width = ref.current?.getBoundingClientRect().width;
            if (width) {
                ref.current.scrollTo({
                    left: value * width,
                    behavior: 'smooth',
                });
            }
        }
    }, [value]);

    return (
        <Box position="relative" {...props}>
            {/* CSS files for react-slick */}
            <link
                rel="stylesheet"
                type="text/css"
                href="https://cdnjs.cloudflare.com/ajax/libs/slick-carousel/1.6.0/slick.min.css"
            />
            <link
                rel="stylesheet"
                type="text/css"
                href="https://cdnjs.cloudflare.com/ajax/libs/slick-carousel/1.6.0/slick-theme.min.css"
            />
            {/* Left Icon */}
            {value > 0 && (
                <IconButton
                    aria-label="left-arrow"
                    colorScheme="messenger"
                    borderRadius={0}
                    position="absolute"
                    left={side}
                    top={top}
                    transform="translate(0%, -50%)"
                    zIndex={2}
                    onClick={() => value > 0 && setValue(value - 1)}
                >
                    <BiLeftArrowAlt />
                </IconButton>
            )}
            {/* Right Icon */}
            {value > 0 && (
                <IconButton
                    aria-label="right-arrow"
                    colorScheme="messenger"
                    borderRadius={0}
                    position="absolute"
                    right={side}
                    top={top}
                    transform="translate(0%, -50%)"
                    zIndex={2}
                    onClick={() => value < cards.length - 1 && setValue(value + 1)}
                >
                    <BiRightArrowAlt />
                </IconButton>
            )}
            {/* Slider */}
            <Box
                ref={ref}
                height="100%"
                width="100%"
                overflow="hidden"
            >
                <Box
                    display="flex"
                    flexDirection="row"
                    flexWrap="nowrap"
                    height="100%"
                >
                    {cards.map((url) => (
                        <Box
                            flex="1 0 100%"
                            height="100%"
                            width="100%"
                            onClick={() => handleOpen(url)}
                        >
                            <Image
                                src={url}
                                height="100%"
                                width="100%"
                                objectFit="cover"
                            />
                        </Box>
                    ))}
                </Box>
            </Box>
            <Modal
                open={open}
                onClose={handleClose}
            >
                <Grid
                    position="relative"
                    width="100vw"
                    height="100vh"
                    margin="auto"
                    onClick={handleClose}
                    cursor="pointer"
                >
                    <Image
                        zIndex={23}
                        src={currSrc}
                        height="90vh"
                        width="90vw"
                        margin="auto"
                        objectFit="contain"
                    />
                </Grid>
            </Modal>
        </Box>
    );
};
